import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Clock, Users, Calendar, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeeklyTours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const whatsappNumber = '917827327243';

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const response = await axios.get(`${API}/tours`);
        setTours(response.data);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, []);

  useEffect(() => {
    if (location.hash && tours.length > 0) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location, tours]);

  const getWhatsAppLink = (tour) => {
    const message = `Hi! I want to book the ${tour.title}. Can you provide more details?`;
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
  };

  if (loading) {
    return (
      <div style={{ padding: '100px 0', textAlign: 'center' }}>
        <p className="body-large">Loading tours...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)', textAlign: 'center' }}>
        <div className="container">
          <h1 className="hero-large" style={{ marginBottom: '24px' }}>Weekly Departure Heritage Tours</h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
            Join our expert-led small-group tours every weekend. Fixed departures, guaranteed quality experiences.
          </p>
        </div>
      </section>

      {/* Tours List */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          {tours.map((tour, index) => (
            <div
              key={tour.tour_id}
              id={tour.tour_id}
              style={{
                marginBottom: index < tours.length - 1 ? '80px' : '0',
                paddingBottom: index < tours.length - 1 ? '80px' : '0',
                borderBottom: index < tours.length - 1 ? '1px solid var(--border-light)' : 'none'
              }}
            >
              <div className="grid-two-column" style={{ gap: '60px' }}>
                {/* Image */}
                <div>
                  <img
                    src={tour.image}
                    alt={tour.title}
                    style={{ width: '100%', height: '500px', objectFit: 'cover' }}
                  />
                </div>

                {/* Content */}
                <div>
                  <span className="body-small" style={{ color: 'var(--text-light)', display: 'block', marginBottom: '8px' }}>
                    {tour.subtitle}
                  </span>
                  <h2 className="heading-1" style={{ marginBottom: '16px' }}>{tour.title}</h2>
                  <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                    {tour.description}
                  </p>

                  {/* Tour Details */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Clock size={20} color="var(--text-primary)" />
                      <div>
                        <p className="body-small" style={{ color: 'var(--text-light)', marginBottom: '4px' }}>Duration</p>
                        <p className="body-regular" style={{ fontWeight: '500' }}>{tour.duration}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Users size={20} color="var(--text-primary)" />
                      <div>
                        <p className="body-small" style={{ color: 'var(--text-light)', marginBottom: '4px' }}>Group Size</p>
                        <p className="body-regular" style={{ fontWeight: '500' }}>{tour.groupSize}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Calendar size={20} color="var(--text-primary)" />
                      <div>
                        <p className="body-small" style={{ color: 'var(--text-light)', marginBottom: '4px' }}>Days</p>
                        <p className="body-regular" style={{ fontWeight: '500' }}>{tour.days.join(', ')}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Clock size={20} color="var(--text-primary)" />
                      <div>
                        <p className="body-small" style={{ color: 'var(--text-light)', marginBottom: '4px' }}>Timings</p>
                        <p className="body-regular" style={{ fontWeight: '500' }}>{tour.timings.join(', ')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Highlights */}
                  <div style={{ marginBottom: '24px' }}>
                    <h3 className="heading-3" style={{ marginBottom: '16px' }}>What You'll Experience</h3>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {tour.highlights.map((highlight, idx) => (
                        <li key={idx} style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                          <CheckCircle size={20} color="var(--text-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                          <span className="body-regular">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Includes & Excludes */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
                    <div>
                      <h4 className="body-regular" style={{ fontWeight: '500', marginBottom: '12px' }}>Included</h4>
                      {tour.includes.map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <CheckCircle size={16} color="var(--status-success)" style={{ flexShrink: 0, marginTop: '2px' }} />
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h4 className="body-regular" style={{ fontWeight: '500', marginBottom: '12px' }}>Not Included</h4>
                      {tour.excludes.map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <XCircle size={16} color="var(--text-light)" style={{ flexShrink: 0, marginTop: '2px' }} />
                          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Best For */}
                  <div style={{ background: 'var(--bg-secondary)', padding: '16px', marginBottom: '32px' }}>
                    <p className="body-small" style={{ color: 'var(--text-light)', marginBottom: '4px' }}>Best For</p>
                    <p className="body-regular">{tour.bestFor}</p>
                  </div>

                  {/* Price & CTA */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div>
                      <p className="body-small" style={{ color: 'var(--text-light)', marginBottom: '4px' }}>Price per person</p>
                      <p className="heading-2">{tour.price}</p>
                    </div>
                    <a
                      href={getWhatsAppLink(tour)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary"
                    >
                      Reserve Your Seat
                      <ArrowRight size={18} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Info Section */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)', textAlign: 'center' }}>
        <div className="container">
          <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
            Monument entry tickets are not included in the tour price. Please purchase tickets separately or our team will assist you.
          </p>
        </div>
      </section>
    </div>
  );
};

export default WeeklyTours;